import _ from 'lodash';
import Moment from 'moment';
import assert from 'assert';
import {
  DATE_TIME_FORMAT,
  EXPIRED_DATE_KIND,
  PERIOD_KIND,
  WORK_HOUR_ZONES,
} from './constants';

import FieldService from '../services/Fields';
import parts from '../utilities/parts';
import moment from 'moment';
import { isNullOrUndefinedOrEmptyStr } from './stringUtil';
import { getMessageValidate, stringToDate } from './validateDateUtil';
import { messageUtility } from './messageUtility';

/**
 * lobalStorage getItem 日付指定情報
 */
const _storageGetItemDateRange = () => {
  const fromDate = sessionStorage.getItem('date_from');
  const toDate = sessionStorage.getItem('date_to');
  const workHourZone = sessionStorage.getItem('work_hour_zone');

  return { fromDate, toDate, workHourZone };
};

/**
 * lobalStorage getItem 日付指定情報 デフォルト値
 */
export const storageGetItemDateRangeDefault = () => {
  const fromDate = Moment().format('YYYY/MM/DD');
  const toDate = Moment().format('YYYY/MM/DD');
  const workHourZone = WORK_HOUR_ZONES.day.key;
  return { fromDate, toDate, workHourZone };
};

/**
 * lobalStorage getItem 日付指定情報 デフォルト値あり
 */
export const storageGetItemDateRange = () => {
  const defaultParam = storageGetItemDateRangeDefault();

  let { fromDate, toDate, workHourZone } = _storageGetItemDateRange();

  fromDate = _.defaultTo(fromDate, defaultParam.fromDate);
  toDate = _.defaultTo(toDate, defaultParam.toDate);

  if (
    ![
      WORK_HOUR_ZONES.day.key,
      WORK_HOUR_ZONES.night.key,
      WORK_HOUR_ZONES.midnight.key,
    ].includes(workHourZone)
  ) {
    workHourZone = defaultParam.workHourZone;
  }

  return { fromDate, toDate, workHourZone };
};

/**
 * lobalStorage setItem 日付指定情報
 */
export const storageSetItemDateRange = ({ fromDate, toDate, workHourZone }) => {
  if (
    parts.someMatchSome(
      [fromDate, toDate, workHourZone],
      [_.isUndefined, _.isNull]
    )
  ) {
    throw new Error('dateSelectorUtility.js storageSetItemDateRange');
  }

  sessionStorage.setItem('date_from', fromDate);
  sessionStorage.setItem('date_to', toDate);
  sessionStorage.setItem('work_hour_zone', workHourZone);
};

/**
 * Global|Panel HeaderDateSelector の componentDidMount時に行われていた
 * FieldServiceからの作業時間帯情報取得
 */
export const fetchFieldServiceInfo = (fieldId, workHourZone) => {
  assert(fieldId, 'getFieldServiceInfo args fieldId');
  assert(_.isString(workHourZone), 'getFieldServiceInfo args workHourZone');

  return FieldService.info(fieldId, {}).then(response => {
    if (!response.result) {
      return;
    }

    const {
      work_hour_zone_day_flag,
      work_hour_zone_night_flag,
      work_hour_zone_midnight_flag,
    } = response.data.entities.field;

    const workHourZoneDayVisible = work_hour_zone_day_flag === 1;
    const workHourZoneNightVisible = work_hour_zone_night_flag === 1;
    const workHourZoneMidnightVisible = work_hour_zone_midnight_flag === 1;

    const availableWorkHourZone = [];
    if (workHourZoneDayVisible) {
      availableWorkHourZone.push('1');
    }
    if (workHourZoneNightVisible) {
      availableWorkHourZone.push('2');
    }
    if (workHourZoneMidnightVisible) {
      availableWorkHourZone.push('3');
    }

    return {
      workHourZone: availableWorkHourZone.includes(workHourZone)
        ? workHourZone
        : availableWorkHourZone[0],
      workHourZoneDayVisible,
      workHourZoneNightVisible,
      workHourZoneMidnightVisible,
    };
  });
};

/**
 * 有効期限の算出
 * @param {string} date: 日付
 * @param {object} obj: 設定したい期間のオブジェクト
 * @return {Date} resultDate: 日付
 */
export const calculationOfValidityPeriod = (date, obj) => {
  if (isEmptyOrValidInputDate(date) === true) {
    return;
  }

  let resultDate = null;
  _.forEach(PERIOD_KIND, it => {
    if (it.ID === obj.period_kind) {
      resultDate = moment(new Date(date).toISOString())
        .add(obj.valid_period, it.PERIOD_KIND)
        .format(DATE_TIME_FORMAT.slashDateFormat);
    }
  });

  return resultDate;
};

/**
 * 入力された日付が不正なデータまたはnull等ではないことを確認する
 * @param {string} date: 日付
 * @return {Boolean} result: true -> 不正なデータまたはnull等, false -> 不正なデータではなくnullではない
 */
export const isEmptyOrValidInputDate = date => {
  if (isNullOrUndefinedOrEmptyStr(date) === true) {
    return true;
  }

  const [year, month, day] = stringToDate(date);
  if (
    isNullOrUndefinedOrEmptyStr(year) === true &&
    isNullOrUndefinedOrEmptyStr(month) === true &&
    isNullOrUndefinedOrEmptyStr(day) === true
  ) {
    return true;
  } else if (
    isNullOrUndefinedOrEmptyStr(getMessageValidate(year, month, day)) === false
  ) {
    return true;
  }

  return false;
};

/**
 * 入力された日付が期限切れではないことを確認する
 * @param {string} targetDate: 比較したい日付
 * @param {string} compareDate: 比較させたい日付（こちらの日付より以前かどうかを確認）
 * @return {Boolean} result: true -> compareDateより過去の日付, false -> compareDateより未来の日付
 */
export const isExpirationDate = (targetDate, compareDate) => {
  return moment(targetDate, DATE_TIME_FORMAT.slashDateFormat).isBefore(
    moment(compareDate, DATE_TIME_FORMAT.slashDateFormat).format(
      DATE_TIME_FORMAT.slashDateFormat
    )
  );
};

/**
 * 取得年月日より有効期限年月日
 * @param {string} takeDate: 取得年月日
 * @param {string} expireDate: 有効期限年月日
 * @return {string} message: 期限切れに関するメッセージ
 */
export const expireDateSameOrLaterThanTakeDate = (takeDate, expireDate) => {
  if (
    isEmptyOrValidInputDate(takeDate) === true ||
    isEmptyOrValidInputDate(expireDate) === true
  ) {
    return null;
  }

  const takeDateMoment = moment(takeDate, DATE_TIME_FORMAT.slashDateFormat);
  const expireDateMoment = moment(expireDate, DATE_TIME_FORMAT.slashDateFormat);
  if (expireDateMoment.isSameOrAfter(takeDateMoment) === false) {
    return messageUtility.getMessage('W-000-011');
  }
};

/**
 * 期限切れチェック
 * @param {string} date: 日付
 * @param {string} licenseName: 資格名称
 * @param {Boolean} isDetailCheck: 期限切れについて詳細に確認するか
 * @param {string} inputInformationName: どこの項目を更新するのか（警告文のメッセージ内に埋め込む項目名）
 * @return {string} message: 期限切れに関するメッセージ
 */
export const validExpirationDate = (
  date,
  licenseName,
  isDetailCheck = false,
  inputInformationName = '入力情報'
) => {
  if (isEmptyOrValidInputDate(date) === true) {
    return null;
  }

  const today = moment(new Date(), DATE_TIME_FORMAT.slashDateFormat);
  if (
    isExpirationDate(date, today.format(DATE_TIME_FORMAT.slashDateFormat)) ===
    true
  ) {
    if (isNullOrUndefinedOrEmptyStr(licenseName) === true) {
      return messageUtility.getMessage('W-000-009');
    }
    return messageUtility.getMessage(
      'W-000-010',
      licenseName,
      inputInformationName
    );
  }

  let message = null;
  if (isDetailCheck === true) {
    _.forEach(EXPIRED_DATE_KIND, it => {
      const temp = moment(date, DATE_TIME_FORMAT.slashDateFormat).subtract(
        it.ID,
        'months'
      );
      if (
        temp.isBefore(today.format(DATE_TIME_FORMAT.slashDateFormat)) === true
      ) {
        if (isNullOrUndefinedOrEmptyStr(licenseName) === true) {
          message = messageUtility.getMessage('W-000-007', it.MESSAGE);
        } else {
          message = messageUtility.getMessage(
            'W-000-008',
            it.MESSAGE,
            licenseName,
            inputInformationName
          );
        }

        return false;
      }
    });
  }

  return message;
};

/**
 * 健康診断期限切れチェック
 * @param {string} date: 日付
 * @param {Object} obj: 健康診断に関するオブジェクト（constファイルのMEDICAL_EXAMINATION_PERIOD_KINDの1レコードなどの想定）
 * @return {string} message: 期限切れに関するメッセージ
 */
export const validMedicalExpirationDate = (date, obj) => {
  if (isEmptyOrValidInputDate(date) === true) {
    return null;
  }

  const compareDate = moment(new Date(date).toISOString())
    .add(obj.VALID_PERIOD, obj.PERIOD_KIND)
    .format(DATE_TIME_FORMAT.slashDateFormat);
  const today = moment(new Date(), DATE_TIME_FORMAT.slashDateFormat).format(
    DATE_TIME_FORMAT.slashDateFormat
  );

  if (isExpirationDate(compareDate, today) === true) {
    return messageUtility.getMessage('W-000-012', obj.MESSAGE);
  }

  let message = null;
  _.forEach(EXPIRED_DATE_KIND, it => {
    const temp = moment(new Date(date).toISOString())
      .add(obj.VALID_PERIOD, obj.PERIOD_KIND)
      .subtract(it.ID, 'months');
    if (temp.isBefore(today) === true) {
      message = messageUtility.getMessage('W-000-013', obj.MESSAGE);
      return false;
    }
  });

  return message;
};

/**
 * エラーメッセージが存在しない場合に警告メッセージが存在するか検索する
 * @param {string} date: 日付
 * @param {string} licenseName: 資格名称
 * @param {Boolean} isDetailCheck: 期限切れについて詳細に確認するか
 * @param {string} errorMessage: エラーメッセージ
 * @param {string} inputInformationName: どこの項目を更新するのか（警告文のメッセージ内に埋め込む項目名）
 * @return {string|null} message: 警告メッセージ
 */
export const wrapperValidExpirationDate = (
  date,
  licenseName,
  isDetailCheck,
  errorMessage,
  inputInformationName
) => {
  if (isNullOrUndefinedOrEmptyStr(errorMessage) === true) {
    return validExpirationDate(
      date,
      licenseName,
      isDetailCheck,
      inputInformationName
    );
  }

  return null;
};

/**
 * 期限切れチェック（簡易的なメッセージを返す）
 * @param {string} date: 日付
 * @param {Boolean} isDetailCheck: 期限切れについて詳細に確認するか
 * @return {string} message: 期限切れに関するメッセージ
 */
export const validExpirationDateReturnSimpleMessage = (
  date,
  isDetailCheck = false,
) => {
  if (isEmptyOrValidInputDate(date) === true) {
    return null;
  }

  const today = moment(new Date(), DATE_TIME_FORMAT.slashDateFormat);
  if (
    isExpirationDate(date, today.format(DATE_TIME_FORMAT.slashDateFormat)) ===
    true
  ) {
    return messageUtility.getMessage('W-000-014');
  }

  let message = null;
  if (isDetailCheck === true) {
    _.forEach(EXPIRED_DATE_KIND, it => {
      const temp = moment(date, DATE_TIME_FORMAT.slashDateFormat).subtract(
        it.ID,
        'months'
      );
      if (
        temp.isBefore(today.format(DATE_TIME_FORMAT.slashDateFormat)) === true
      ) {
        message = messageUtility.getMessage('W-000-015', it.MESSAGE);
        return false;
      }
    });
  }

  return message;
};



// WEBPACK FOOTER //
// ./src/utilities/dateSelectorUtility.js